import { useState, useEffect } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Eye, Download, Trash2, CloudUpload} from "lucide-react";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

export default function DocumentUpload() {
  const [files, setFiles] = useState([]);
  const [filesFromServer, setFilesFromServer] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("");
  const [filterType, setFilterType] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteFile, setDeleteFile] = useState(null);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  let user = null;
  try {
    const stored = localStorage.getItem("user");
    user = stored && stored !== "undefined" ? JSON.parse(stored) : null;
  } catch {
    user = null;
  }

  const documentTypes = [
    "Aadhaar Card", "PAN Card", "Prescription", "Lab Report",
    "X-Ray", "MRI Scan", "Discharge Summary", "Medical Certificate",
    "Insurance Document", "Medical Bills"
  ];

  const typeIcons = {
    "Aadhaar Card": "🪪", "PAN Card": "🆔", "Prescription": "💊",
    "Lab Report": "🧪", "X-Ray": "🩻", "MRI Scan": "🧠",
    "Discharge Summary": "📄", "Medical Certificate": "📜",
    "Insurance Document": "🛡️", "Medical Bills": "💰"
  };

  const fetchFiles = async () => {
    try {
      const res = await axios.get(`${API}/api/files/${user.id}`);
      setFilesFromServer(res.data.files);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchFiles(); }, []);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles((prev) => [...prev, ...selectedFiles]);
  };

  const removeFile = (index) => setFiles(files.filter((_, i) => i !== index));
  const thStyle = {
    padding: "12px",
    textAlign: "left",
    fontSize: "0.85rem",
    color: "#1a3a52"
  };

  const tdStyle = {
    padding: "12px",
    fontSize: "0.85rem"
  };

  const handleUpload = async () => {
    if (!docType) { toast.warning("Please select document type"); return; }
    if (files.length === 0) { toast.warning("Please select files"); return; }
    if (!privacyAccepted) {toast.warning("Please accept the Privacy Policy before uploading.");return;}

    const formData = new FormData();
    files.forEach((file) => formData.append("documents", file));
    formData.append("user_id", user.id);
    formData.append("document_type", docType);

    try {
      setUploading(true);
      await axios.post(`${API}/api/upload`, formData);
      toast.success("Uploaded successfully!");
      setFiles([]);
      setDocType("");
      setPrivacyAccepted(false);
      fetchFiles();
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/api/delete/${deleteFile}`);
      toast.success("Deleted");
      fetchFiles();
    } catch {
      toast.error("Delete failed");
    } finally {
      setDeleteFile(null);
    }
  };

  const filteredFiles = filesFromServer.filter((file) => {
    const matchesType =
      filterType === "All" || file.document_type === filterType;

    const matchesSearch =
      file.name.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesType && matchesSearch;
  });

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Lora:wght@400;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');

        .doc-vault-root {
          min-height: 100vh;
          background: linear-gradient(135deg, #e8f4fd 0%, #d6eaf8 40%, #e8f5e9 100%);
          padding: 40px 24px;
          font-family: 'DM Sans', sans-serif;
          position: relative;
          overflow: hidden;
        }

        .doc-vault-root::before {
          content: '';
          position: fixed;
          top: -120px; left: -120px;
          width: 400px; height: 400px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(100,181,246,0.18) 0%, transparent 70%);
          pointer-events: none;
        }

        .doc-vault-root::after {
          content: '';
          position: fixed;
          bottom: -80px; right: -80px;
          width: 300px; height: 300px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(129,212,250,0.14) 0%, transparent 70%);
          pointer-events: none;
        }

        .floating-dot {
          position: fixed;
          border-radius: 50%;
          background: rgba(100, 181, 246, 0.12);
          pointer-events: none;
        }

        .vault-title {
          font-family: 'Lora', serif;
          font-size: 2rem;
          font-weight: 700;
          color: #1a3a52;
          margin-bottom: 8px;
          letter-spacing: -0.3px;
        }

        .vault-subtitle {
          color: #5a8fa8;
          font-size: 0.95rem;
          margin-bottom: 32px;
          font-weight: 400;
        }

        .upload-panel {
          background: rgba(255,255,255,0.72);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid rgba(255,255,255,0.85);
          border-radius: 20px;
          padding: 28px 32px;
          margin-bottom: 32px;
          box-shadow: 0 4px 32px rgba(100,181,246,0.08), 0 1px 4px rgba(0,0,0,0.04);
        }

        .upload-row {
          display: flex;
          flex-wrap: wrap;
          gap: 14px;
          align-items: center;
        }

        .doc-select {
          padding: 10px 16px;
          border: 1.5px solid #b3d8f0;
          border-radius: 12px;
          background: rgba(255,255,255,0.9);
          color: #1a3a52;
          font-family: 'DM Sans', sans-serif;
          font-size: 0.9rem;
          outline: none;
          cursor: pointer;
          transition: border-color 0.2s;
        }

        .doc-select:focus {
          border-color: #42a5d4;
        }

        .file-input-label {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 18px;
          border: 1.5px dashed #87ceeb;
          border-radius: 12px;
          background: rgba(135,206,235,0.08);
          color: #2a7fa8;
          font-size: 0.9rem;
          cursor: pointer;
          transition: background 0.2s, border-color 0.2s;
          font-family: 'DM Sans', sans-serif;
        }

        .file-input-label:hover {
          background: rgba(135,206,235,0.16);
          border-color: #42a5d4;
        }

        .file-input-label input { display: none; }

        .upload-btn {
          padding: 10px 24px;
          border-radius: 12px;
          border: none;
          font-family: 'DM Sans', sans-serif;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .upload-btn.active {
          background: linear-gradient(135deg, #2196f3, #0d6eae);
          color: white;
          box-shadow: 0 4px 14px rgba(33,150,243,0.3);
        }

        .upload-btn.active:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(33,150,243,0.35);
        }

        .upload-btn.disabled {
          background: #d0e8f5;
          color: #94b8cc;
          cursor: not-allowed;
        }

        .pending-file {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          margin-top: 10px;
          padding: 6px 12px;
          background: rgba(33,150,243,0.08);
          border-radius: 8px;
          font-size: 0.83rem;
          color: #1a5276;
        }

        .pending-file button {
          background: none;
          border: none;
          cursor: pointer;
          font-size: 0.9rem;
          line-height: 1;
          color: #e74c3c;
        }

        /* Filter Pills */
        .filter-row {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 24px;
        }

        .filter-pill {
          padding: 7px 16px;
          border-radius: 50px;
          border: 1.5px solid transparent;
          font-family: 'DM Sans', sans-serif;
          font-size: 0.82rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.18s;
        }

        .filter-pill.active {
          background: #1565c0;
          color: white;
          border-color: #1565c0;
          box-shadow: 0 3px 12px rgba(21,101,192,0.25);
        }

        .filter-pill.inactive {
          background: rgba(255,255,255,0.75);
          color: #3a6d8c;
          border-color: rgba(100,181,246,0.3);
        }

        .filter-pill.inactive:hover {
          background: rgba(255,255,255,0.95);
          border-color: #64b5f6;
        }

        /* Empty State */
        .empty-state-card {
          background: rgba(255,255,255,0.75);
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
          border: 1px solid rgba(255,255,255,0.9);
          border-radius: 24px;
          padding: 72px 40px;
          text-align: center;
          box-shadow: 0 4px 40px rgba(100,181,246,0.1), 0 1px 4px rgba(0,0,0,0.03);
          max-width: 520px;
          margin: 16px auto;
        }

        .empty-icon-wrap {
          width: 80px;
          height: 96px;
          margin: 0 auto 24px;
          position: relative;
        }

        .empty-doc-icon {
          width: 80px;
          height: 96px;
          background: linear-gradient(145deg, #e8f4fd, #d0e8f7);
          border-radius: 10px 10px 10px 10px;
          position: relative;
          box-shadow: 0 4px 20px rgba(100,181,246,0.2);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .empty-doc-icon::before {
          content: '';
          position: absolute;
          top: 0; right: 0;
          width: 22px; height: 22px;
          background: linear-gradient(225deg, #b3d8f0 50%, transparent 50%);
          border-radius: 0 8px 0 0;
        }

        .empty-doc-lines {
          display: flex;
          flex-direction: column;
          gap: 5px;
          padding-top: 14px;
        }

        .empty-doc-line {
          height: 4px;
          border-radius: 3px;
          background: rgba(100,181,246,0.4);
        }

        .empty-state-title {
          font-family: 'Lora', serif;
          font-size: 1.35rem;
          font-weight: 700;
          color: #1a3a52;
          margin-bottom: 12px;
        }

        .empty-state-desc {
          color: #7baec4;
          font-size: 0.9rem;
          line-height: 1.65;
          max-width: 320px;
          margin: 0 auto;
        }

        /* File Cards */
        .files-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 18px;
        }

        .file-card {
          background: rgba(255,255,255,0.75);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
          border: 1px solid rgba(255,255,255,0.9);
          border-radius: 18px;
          padding: 20px;
          box-shadow: 0 2px 20px rgba(100,181,246,0.08);
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .file-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 6px 28px rgba(100,181,246,0.16);
        }

        .file-card-header {
          display: flex;
          align-items: center;
          gap: 14px;
          margin-bottom: 14px;
        }

        .file-icon-badge {
          font-size: 1.8rem;
          width: 48px;
          height: 48px;
          background: linear-gradient(135deg, #e8f4fd, #d4ecf7);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .file-name {
          font-weight: 600;
          color: #1a3a52;
          font-size: 0.92rem;
          word-break: break-word;
        }

        .file-type-tag {
          font-size: 0.78rem;
          color: #2a90c8;
          font-weight: 500;
          margin-top: 2px;
        }

        .file-actions {
          display: flex;
          gap: 10px;
          padding-top: 12px;
          border-top: 1px solid rgba(100,181,246,0.12);
        }

        .file-action-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: 10px;
          border: none;
          cursor: pointer;
          transition: all 0.18s;
          text-decoration: none;
          font-size: 1rem;
          background: rgba(33,150,243,0.08);
          color: #1565c0;
        }

        .file-action-btn:hover {
          background: rgba(33,150,243,0.16);
          transform: scale(1.08);
        }

        .file-action-btn.danger {
          background: rgba(231,76,60,0.07);
          color: #c0392b;
        }

        .file-action-btn.danger:hover {
          background: rgba(231,76,60,0.14);
        }
      `}</style>

      <div className="doc-vault-root">
        {/* Ambient floating dots */}
        <div className="floating-dot" style={{ width: 180, height: 180, top: '8%', right: '12%' }} />
        <div className="floating-dot" style={{ width: 100, height: 100, top: '55%', left: '5%' }} />
        <div className="floating-dot" style={{ width: 60, height: 60, bottom: '15%', right: '30%' }} />

        <ToastContainer position="top-right" toastStyle={{ borderRadius: 14, fontFamily: 'DM Sans, sans-serif' }} />

        <h1 className="vault-title">📂 Document Vault</h1>
        <p className="vault-subtitle">Securely store and manage your medical documents</p>

        {/* Upload Panel */}
        <div className="upload-panel">
          <div className="upload-row">
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              className="doc-select"
            >
              <option value="">Select Document Type</option>
              {documentTypes.map((type, i) => (
                <option key={i} value={type}>{type}</option>
              ))}
            </select>

            <label className="file-input-label">
              <CloudUpload size={16} />
              Choose Files
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                disabled={!docType}
              />
            </label>

            <button
              onClick={handleUpload}
              disabled={
  uploading ||
  !docType ||
  files.length === 0 ||
  !privacyAccepted
}
              className={`upload-btn ${
  uploading ||
  !docType ||
  files.length === 0 ||
  !privacyAccepted
    ? "disabled"
    : "active"
}`}
            >
              <CloudUpload size={15} />
              {uploading ? "Uploading..." : "Upload"}
            </button>
          </div>

          {files.length > 0 && (
            <div style={{ marginTop: 14, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {files.map((file, i) => (
                <div key={i} className="pending-file">
                  📎 {file.name}
                  <button onClick={() => removeFile(i)}>✕</button>
                </div>
              ))}
            </div>
          )}
          <div
  style={{
    marginTop: "18px",
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    fontSize: "0.9rem",
    color: "#1a3a52",
  }}
>
  <input
    type="checkbox"
    id="privacyConsent"
    checked={privacyAccepted}
    onChange={(e) => setPrivacyAccepted(e.target.checked)}
    style={{
      marginTop: "3px",
      cursor: "pointer",
    }}
  />

  <label htmlFor="privacyConsent" style={{ lineHeight: 1.6 }}>
    I confirm that these documents belong to me or I am authorized to
    upload them. I have read and agree to the{" "}
    <a
      href="/privacy-policy"
      target="_blank"
      rel="noopener noreferrer"
      style={{
        color: "#1976d2",
        textDecoration: "none",
        fontWeight: 600,
      }}
    >
      Privacy Policy
    </a>{" "}
    and consent to the secure storage and processing of my medical
    documents.
  </label>
</div>
        </div>

        {/* Filter Pills */}
        <div style={{ display: "flex", gap: "12px", marginBottom: "24px", flexWrap: "wrap" }}>

          {/* 🔍 Search */}
          <input
            type="text"
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              padding: "10px 14px",
              borderRadius: "12px",
              border: "1.5px solid #b3d8f0",
              outline: "none",
              minWidth: "220px",
              fontSize: "0.9rem"
            }}
          />

          {/* 📂 Filter dropdown */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            style={{
              padding: "10px 14px",
              borderRadius: "12px",
              border: "1.5px solid #b3d8f0",
              fontSize: "0.9rem",
              cursor: "pointer"
            }}
          >
            <option value="All">All Types</option>
            {documentTypes.map((type, i) => (
              <option key={i} value={type}>{type}</option>
            ))}
          </select>

        </div>

        {/* Empty State */}
        {filteredFiles.length === 0 ? (
          <div className="empty-state-card">
            <div className="empty-icon-wrap">
              <div className="empty-doc-icon">
                <div className="empty-doc-lines">
                  <div className="empty-doc-line" style={{ width: 36 }} />
                  <div className="empty-doc-line" style={{ width: 28 }} />
                  <div className="empty-doc-line" style={{ width: 32 }} />
                  <div className="empty-doc-line" style={{ width: 20 }} />
                </div>
              </div>
            </div>

            <h2 className="empty-state-title">No Documents Found</h2>
            <p className="empty-state-desc">
              {filterType === "All"
                ? "Upload your medical documents to keep them securely organized and accessible anytime."
                : `No ${filterType} documents have been uploaded yet. Upload one to get started.`}
            </p>
          </div>
        ) : (
          /* File Grid */
          <div style={{ overflowX: "auto", maxHeight: "500px", overflowY: "auto" }}>
            <table style={{
              width: "100%",
              borderCollapse: "collapse",
              background: "rgba(255,255,255,0.75)",
              borderRadius: "12px",
              overflow: "hidden"
            }}>

              {/* Header */}
              <thead style={{ background: "#e3f2fd", position: "sticky", top: 0, zIndex: 10 }}>
                <tr>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>File Name</th>
                  <th style={thStyle}>Category</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>

              {/* Body */}
              <tbody>
                {filteredFiles.map((file, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #e0e0e0" }}>

                    {/* Icon */}
                    <td style={tdStyle}>
                      {typeIcons[file.document_type] || "📁"}
                    </td>

                    {/* Name */}
                    <td style={tdStyle}>{file.name}</td>

                    {/* Type */}
                    <td style={{ ...tdStyle, color: "#1976d2", fontWeight: "500" }}>
                      {file.document_type}
                    </td>

                    {/* Actions */}
                    <td style={tdStyle}>
                      <div style={{ display: "flex", gap: "8px" }}>

                        <a href={file.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:scale-110 transition">
                          <Eye size={18} />
                        </a>


                        {/* Download */}
                        <a
                          href={`${API}/api/download/${file.name}`}
                          className="text-green-600 hover:scale-110 transition"
                        >
                          <Download size={18} />
                        </a>


                        {/* Delete */}

                        <button
                          onClick={() => setDeleteFile(file.name)}
                          className="text-red-600 hover:scale-110 transition"
                        >
                          <Trash2 size={18} />
                        </button>



                        {deleteFile && (
                          <div
                            style={{
                              position: "fixed",
                              inset: 0,
                              background: "rgba(0,0,0,0.35)",
                              backdropFilter: "blur(6px)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              zIndex: 1000
                            }}
                          >
                            <div
                              style={{
                                width: "320px",
                                background: "rgba(255,255,255,0.9)",
                                borderRadius: "16px",
                                padding: "24px",
                                textAlign: "center",
                                boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
                                animation: "fadeIn 0.2s ease"
                              }}
                            >
                              {/* Icon */}
                              <div
                                style={{
                                  fontSize: "32px",
                                  marginBottom: "12px"
                                }}
                              >
                                ⚠️
                              </div>

                              {/* Title */}
                              <h3
                                style={{
                                  fontSize: "1.1rem",
                                  fontWeight: "600",
                                  color: "#1a3a52",
                                  marginBottom: "8px"
                                }}
                              >
                                Delete Document
                              </h3>

                              {/* Description */}
                              <p
                                style={{
                                  fontSize: "0.9rem",
                                  color: "#6b8fa3",
                                  marginBottom: "20px"
                                }}
                              >
                                Are you sure you want to delete this file?
                                <br />
                                This action cannot be undone.
                              </p>

                              {/* Buttons */}
                              <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
                                <button
                                  onClick={() => setDeleteFile(null)}
                                  style={{
                                    padding: "8px 16px",
                                    borderRadius: "10px",
                                    border: "1px solid #cfe3f0",
                                    background: "white",
                                    color: "#1a3a52",
                                    cursor: "pointer"
                                  }}
                                >
                                  Cancel
                                </button>

                                <button
                                  onClick={confirmDelete}
                                  style={{
                                    padding: "8px 16px",
                                    borderRadius: "10px",
                                    border: "none",
                                    background: "linear-gradient(135deg, #e74c3c, #c0392b)",
                                    color: "white",
                                    cursor: "pointer",
                                    boxShadow: "0 4px 12px rgba(231,76,60,0.3)"
                                  }}
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>

                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        )}
      </div>
    </>
  );
}